import { createSlice } from "@reduxjs/toolkit"
import { CampaignDetail } from "types/campaign"
import { CampaignApplication } from "types/campaign/applications"
import { Influencer, SocialAccount } from "types/influencer"

export interface CampaignInviteCreatorPreview {
    uuid: string
    name: string
    handle: string
    profile_picture_url: string | null
    platform?: string
    followers_count?: number
}

export interface UIComponents {
    influencerPostModal: {
        postId: string,
        influencer?: Influencer,
        socialAccount: SocialAccount,
        collaborationId?: string,
        status?: 'pending' | 'approved' | 'rejected' | 'processing',
        milestoneStep?: string | null,
        preventAction?: boolean,
    } | null,
    socialPostModal: {
        postId: string,
        socialAccount: SocialAccount,
        influencer?: Influencer,
    } | null,
    paymentsOffcanvas: boolean,
    applicationOffcanvas: {
        campaignId: string,
        influencerId?: string,
        socialAccountId?: string,
        status: 'pending' | 'accepted' | 'rejected' | 'processing'
        approve?: () => void,
        reject?: () => void
    } | null,
    applicationView: {
        applicationId?: string,
        campaignId?: string,
        socialAccountId: string,
    } | null,
    socialAccountView: {
        socialAccountId: string,
        influencerId?: string,
    } | null,
    socialAccountOnboarding: {
        message: string,
        handle: string,
        profileUrl: string,
    } | null,
    postPerformanceOffcanvas: {
        collaborationId: string,
    } | null,
    error: {
        code: number,
        message: string
    } | null,
    header: {
        campaignData?: CampaignDetail | null,
        detailPage?: boolean,
    } | null,
    lastActiveCampaignTab: 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators' | 'invites',
    campaignsLocation: {pathname: string, search?: string},
    notificationPreferences: boolean,
    approveApplicationCostModal: {
        application: CampaignApplication;
        isOpen: boolean;
    } | null,
    shortlistExitAnimation: {
        campaignId: string,
        applicationId: string,
    } | null,
    shortlistTabPulse: number,
    campaignInviteModal: {
        socialAccountIds: string[],
        creators?: CampaignInviteCreatorPreview[],
    } | null,
}

const initialState: UIComponents = {
    influencerPostModal : null,
    socialPostModal: null,
    paymentsOffcanvas: false,
    applicationOffcanvas: null,
    applicationView: null,
    socialAccountView: null,
    error: null,
    socialAccountOnboarding: null,
    header: {
        campaignData: null,
        detailPage: false,
    },
    postPerformanceOffcanvas: null,
    lastActiveCampaignTab: 'applications',
    campaignsLocation: {pathname: '/campaign'},
    notificationPreferences: false,
    approveApplicationCostModal: null,
    shortlistExitAnimation: null,
    shortlistTabPulse: 0,
    campaignInviteModal: null,
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        showInfluencerPostModal: (state: UIComponents, action: { payload: { postId: string, influencer?: Influencer, socialAccount: SocialAccount, collaborationId?: string, status?: 'pending' | 'approved' | 'rejected' | 'processing', milestoneStep?: string | null, preventAction?: boolean } }) => {
                state.influencerPostModal = action.payload;
            },
        closeInfluencerPostModal: (state: UIComponents) => {
            state.influencerPostModal = null;
        },
        showSocialPostModal: (state: UIComponents, action: { payload: { postId: string, influencer?: Influencer, socialAccount: SocialAccount } }) => {
            state.socialPostModal = action.payload;
        },
        closeSocialPostModal: (state: UIComponents) => {
            state.socialPostModal = null;
        },
        togglePaymentsOffcanvas: (state: UIComponents, action : {payload: boolean}) => {
            state.paymentsOffcanvas = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setHeaderCampaignData: (state, { payload }) => {
            const { campaign, detailPage } = payload;
            state.header = {
                ...(state.header ?? {}),
                campaignData: campaign,
                detailPage,
            };
        },
        toggleApplicationOffcanvas: (state, action : {payload: {campaignId: string, influencerId?: string, socialAccountId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing', approve?: () => void, reject?: () => void} | null}) => {
            state.applicationOffcanvas = action.payload;
        },
        toggleApplicationView: (state, { payload }: { payload: { applicationId?: string, campaignId?: string, socialAccountId: string } | null }) => {
            state.applicationView = payload;
        },
        toggleSocialAccountView: (state, { payload }: { payload: { socialAccountId: string, influencerId?: string } | null }) => {
            state.socialAccountView = payload;
        },
        toggleApproveApplicationCostModal: (state, { payload }: { payload: { application: CampaignApplication; isOpen: boolean } | null }) => {
            state.approveApplicationCostModal = payload;
        },
        togglePostPerformanceOffcanvas: (state, action: {payload: {collaborationId: string} | null}) => {
            state.postPerformanceOffcanvas = action.payload
        },
        setLastActiveCampaignTab : (state, action: {payload: 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators' | 'invites'}) => {
            state.lastActiveCampaignTab = action.payload;
        }, 
        setCampaignsLocation: (state, action: {payload: {pathname: string, search?: string}}) => {
            state.campaignsLocation = action.payload
        },
        toggleNotificationPreferencesModal: (state, {payload}) => {
            state.notificationPreferences = payload;
        },
        startShortlistExit: (
            state,
            { payload }: { payload: { campaignId: string; applicationId: string } }
        ) => {
            state.shortlistExitAnimation = payload;
        },
        clearShortlistExit: (state) => {
            state.shortlistExitAnimation = null;
        },
        pulseShortlistTab: (state) => {
            state.shortlistTabPulse += 1;
        },
        showCampaignInviteModal: (
            state,
            { payload }: { payload: { socialAccountIds: string[]; creators?: CampaignInviteCreatorPreview[] } }
        ) => {
            state.campaignInviteModal = payload;
        },
        closeCampaignInviteModal: (state) => {
            state.campaignInviteModal = null;
        },
        toggleSocialAccountOnboarding: (state, { payload }: { payload: { message: string, handle: string, profileUrl: string } | null }) => {
            state.socialAccountOnboarding = payload;
        },
    }
})

export const {
    showInfluencerPostModal,
    closeInfluencerPostModal,
    showSocialPostModal,
    closeSocialPostModal,
    togglePaymentsOffcanvas,
    setError,
    setHeaderCampaignData,
    toggleApplicationOffcanvas,
    toggleApplicationView,
    toggleSocialAccountView,
    toggleApproveApplicationCostModal,
    togglePostPerformanceOffcanvas,
    setCampaignsLocation,
    setLastActiveCampaignTab,
    toggleNotificationPreferencesModal,
    startShortlistExit,
    clearShortlistExit,
    pulseShortlistTab,
    showCampaignInviteModal,
    closeCampaignInviteModal,
    toggleSocialAccountOnboarding,
} = uiSlice.actions;

export default uiSlice.reducer;
